'use client'
import { ReactNode } from 'react'
import { useRelativeDate } from '../_hooks/use-relative-date'
import { useEstimatedReadingTime } from '../_hooks/use-estimated-reading-time'
import styled from '@emotion/styled'

const PrimaryImage = styled.div`
  .entry-image-container {
    aspect-ratio: 16 / 9;
  }

  .entry-image {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

interface Props {
  children: ReactNode
  authorAvatarUrl: string
  headerImageUrl: string
  headerImageAlt: string
  publishedAt: Date
  postTitle: string
}

export const BlogPost = ({
  authorAvatarUrl,
  publishedAt,
  postTitle,
  headerImageUrl,
  headerImageAlt,
  children,
}: Props) => {
  const relativeDate = useRelativeDate(publishedAt)
  const estimatedReadingTime = useEstimatedReadingTime(JSON.stringify(children))

  return (
    <>
      <Container itemScope itemType="https://schema.org/Article" avatarUrl={authorAvatarUrl}>
        <PrimaryImage>
          <div className="entry-image-container">
            <img className="entry-image" src={headerImageUrl} alt={headerImageAlt} />
          </div>
        </PrimaryImage>
        <h1 itemProp="name">{postTitle}</h1>

        <div className="post-header">
          <div className="header-content">
            <div className="author-container">
              <div className="details">
                Publicerad{' '}
                <time itemProp="datePublished" dateTime={publishedAt.toISOString().split('T')[0]}>
                  {relativeDate}
                </time>{' '}
                · {estimatedReadingTime.minutes} min läsning
              </div>
            </div>
          </div>
        </div>

        {children}
        <div className="end-line" />
      </Container>
    </>
  )
}

const Container = styled.div<{ avatarUrl: string }>`
  padding: 0 21rem;

  margin-top: 8rem;
  margin-bottom: 10rem;

  font-size: 1.25rem;

  img {
    margin-bottom: 2rem;
  }

  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }

  p,
  ol li,
  ul li {
    margin-bottom: 1.5rem;
    line-height: 2rem;
  }

  ul,
  ol {
    list-style-position: inside;
  }

  ul li,
  ol li {
    padding: 0.25rem 0;
  }

  .post-header {
    margin: 2rem 0;

    display: flex;
    flex-direction: row;
    align-items: center;

    gap: 1.25rem;

    border-top: 1px solid ${({ theme }) => theme.onBackgroundLightest};
    border-bottom: 1px solid ${({ theme }) => theme.onBackgroundLightest};

    padding: 1rem 0;

    .avatar-container {
      position: relative;

      min-width: 3.5rem;
      aspect-ratio: 1;
      border-radius: 50%;

      background-image: url(${({ avatarUrl }) => avatarUrl});
      background-size: cover;
    }

    .badge {
      z-index: 5;
      position: absolute;
      top: 2rem;
      right: -0.25rem;

      color: ${({ theme }) => theme.primaryLight};
    }

    .badge-background {
      z-index: 4;
      position: absolute;
      top: 2.125rem;
      right: -0.125rem;

      width: 1rem;
      aspect-ratio: 1;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.onPrimary};
    }

    .author-container {
      .author {
        font-size: 1rem;
      }

      .details {
        padding: 1rem 0;
        font-size: 0.85rem;
        color: ${({ theme }) => theme.onBackgroundLight};
      }
    }

    .header-content {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  .end-line {
    margin-top: 2rem;
    border-bottom: 1px solid ${({ theme }) => theme.onBackgroundLightest};
  }

  @media (max-width: 1200px) {
    padding: 0 10rem;
    h1 {
      font-size: 2rem;
    }
  }

  @media (max-width: 900px) {
    padding: 0 5rem;

    h1 {
      font-size: 2rem;
    }
  }

  @media (max-width: 600px) {
    padding: 0;

    h1 {
      font-size: 2rem;
      padding: 0 2rem;
    }

    h2 {
      padding: 0 2rem;
    }

    p,
    ol li,
    ul li {
      padding: 0 2rem;
    }

    .post-header {
      padding: 1rem 2rem;
      margin: 2rem 0;
    }
  }
`
